import { useEffect, useRef, useState } from "react";
import checkIcon from "../../../assets/icons/check-icon.svg";
import Error from "../../../components/Error";
import BottomBackToMain from "../../../components/interpretation/BottomBackToMain";
import QuickView from "../../../components/interpretation/QuickView";
import Table from "../../../components/interpretation/Table";
import Loader from "../../../components/AppLoader";
import useSummaryTablesData, {
    TablesTypes,
} from "../../../hooks/useSummaryTablesData";
import usePrompt from "../../../hooks/usePrompt";
import { useInterpretationContext } from "../../../context/InterpretationContext";
import {
    cancelInterpretation,
    confirmInterpretation,
} from "../../../api/interpretation";
import useTestData from "../../../hooks/useTestData";
import { ROUTES } from "../../config";
import { twMerge } from "tailwind-merge";

export default function SummaryTable() {
    const [tablesToDisplay, setTablesToDisplay] = useState<TablesTypes>(
        TablesTypes.PEAK_VALUES
    );

    const { isInterpretationDirty } = useInterpretationContext();
    const { value: testData } = useTestData();
    const testNotCompleted = testData?.testStatus === "Not completed";
    const isWheelNavigatePauseRef = useRef<boolean>(false);

    const { tables, loading, error } = useSummaryTablesData({
        type: tablesToDisplay,
    });

    const tableTypeItems = Object.keys(TablesTypes) as Array<
        keyof typeof TablesTypes
    >;

    // create wheel event with log when user scrolls on the edge of the page
    const handleWheel = (event: WheelEvent) => {
        if (isWheelNavigatePauseRef.current) {
            return;
        }
        const { scrollTop, scrollHeight, clientHeight } =
            document.documentElement;
        const direction = event.deltaY > 0 ? "down" : "up";
        const minDeltaLimit = 250;
        const hasEnoughDelta = Math.abs(event.deltaY) > minDeltaLimit;

        const needToNavigate =
            hasEnoughDelta &&
            (scrollTop + clientHeight >= scrollHeight ||
                (scrollTop === 0 && direction === "up"));

        if (!needToNavigate) return;

        isWheelNavigatePauseRef.current = true;
        setTimeout(() => {
            isWheelNavigatePauseRef.current = false;
        }, 500);

        const currentTableIndex =
            Object.values(TablesTypes).indexOf(tablesToDisplay);
        const newIndex =
            direction === "down"
                ? Math.min(currentTableIndex + 1, tableTypeItems.length - 1)
                : Math.max(currentTableIndex - 1, 0);

        setTablesToDisplay(TablesTypes[tableTypeItems[newIndex]]);
    };

    useEffect(() => {
        document.addEventListener("wheel", handleWheel);
        return () => {
            document.removeEventListener("wheel", handleWheel);
        };
    }, [tablesToDisplay]);

    usePrompt({
        isDirty: false,
        isInterpretationDirty,
        onConfirm: () => handleDialogConfirm(),
        onCancel: () => handleDialogCancel(),
    });

    const handleDialogConfirm = async () => {
        await confirmInterpretation(testData?.testId);
    };

    const handleDialogCancel = async () => {
        await cancelInterpretation(testData?.testId);
    };

    if (loading) return <Loader />;

    if (error) return <Error />;
    if (!tables) return null;

    return (
        <div className="flex xl:pl-5 gap-6 pb-bottomPanel mb-0">
            <div className="flex flex-col gap-3 h-full w-leftSidebar shrink-0">
                <QuickView />
                <div className="text-blue-dark flex flex-col gap-3 mt-2">
                    <h6 className="text-sm xl:text-[15px] leading-snug xl:leading-7 font-bold">
                        Summary tables to display
                    </h6>
                    <div className="flex flex-col">
                        {tableTypeItems.map((typeKey) => {
                            const tableType = TablesTypes[typeKey];
                            const active = tablesToDisplay === tableType;
                            return (
                                <button
                                    key={typeKey}
                                    type="button"
                                    className={`font-manrope text-[13px] xl:text-sm w-full h-9 xl:h-10 flex items-center text-start gap-0.5 xl:gap-1.5 px-1 xl:px-3 ${
                                        active
                                            ? "bg-[#82BAFD59] border border-blue-dark font-bold rounded-sm"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        setTablesToDisplay(tableType)
                                    }
                                >
                                    <div className="w-4 h-4">
                                        {active ? (
                                            <img
                                                src={checkIcon}
                                                alt="Check Icon"
                                                className="w-full h-full"
                                            />
                                        ) : null}
                                    </div>
                                    {tableType}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
            <div className="flex flex-col gap-10 h-full w-full">
                <div className="flex flex-col gap-2.5  h-full xl:h-[730px] w-full">
                    <h1 className="text-blue-dark font-bold text-[22px] leading-9 h-[30px]">
                        Summary Tables - {tablesToDisplay}{" "}
                    </h1>
                    <div
                        className={twMerge(
                            "flex items-stretch gap-8",
                            tablesToDisplay === TablesTypes.PEAK_VALUES &&
                                "gap-x-5"
                        )}
                    >
                        <div className="h-full">
                            {tables[0].map((tableData) => (
                                <Table {...tableData} />
                            ))}
                        </div>
                        {tables[1] ? (
                            <div className="h-full flex flex-col justify-between">
                                {tables[1].map((tableData) => (
                                    <Table {...tableData} />
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
                <BottomBackToMain
                    isFixed
                    label={testNotCompleted ? "Interpretation" : undefined}
                    backButtonSettings={
                        testNotCompleted
                            ? {
                                  href: `/test/${testData?.testId}/${ROUTES.INTERPRETATION.path}/${ROUTES.INTERPRETATION.subroutes.ANAEROBIC_THRESHOLD.path}`,
                              }
                            : undefined
                    }
                />
            </div>
        </div>
    );
}
